/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_difference.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns the values that appear in the first field, except those that appear in the second. Returns `null` if the first expression is null (nothing to remove) or the value of the first field if the second is null.
 *
 * @example
 * ROW values = ["a", "b", "c"], element = "a"
 * | EVAL result = mv_difference(values, element)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_difference',
  signatures: [
    {
      params: [
        {
          name: 'field1',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'field2',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'field2',
          type: 'cartesian_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'field2',
          type: 'cartesian_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'date',
          optional: false,
        },
        {
          name: 'field2',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'field2',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'double',
          optional: false,
        },
        {
          name: 'field2',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'field2',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geo_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geo_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geohash',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geohex',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geotile',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'integer',
          optional: false,
        },
        {
          name: 'field2',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'ip',
          optional: false,
        },
        {
          name: 'field2',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'field2',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'field2',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'long',
          optional: false,
        },
        {
          name: 'field2',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'text',
          optional: false,
        },
        {
          name: 'field2',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'text',
          optional: false,
        },
        {
          name: 'field2',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'field2',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'version',
          optional: false,
        },
        {
          name: 'field2',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
